import ChevronRight from "@mui/icons-material/ChevronRight";
import Print from "@mui/icons-material/PrintOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import { GetWasteContainerQuery, LabelFormat } from "generated-graphql/graphql";
import { ReactElement } from "react";

import { SectionCard } from "../containerSections";
import { LabelPreviewNotices } from "./LabelPreview";
import { LabelPreviewStage } from "./LabelPreviewStage";
import { labelSummarySentence } from "./labelSummary";

/** The fields this section needs off the container detail query -- a subset
 * of `WasteContainerDetail`'s full row, so its test fixtures don't have to
 * carry fields (capacity, clock, fill events, ...) it never reads. */
export type ContainerLabelSectionContainer = Pick<
  NonNullable<GetWasteContainerQuery["wasteContainer"]>,
  "id" | "containerLabel" | "containerType" | "capacity" | "capacityUnit" | "label" | "labelNote"
>;

/** Zoom bounds for this section's inline preview, which the specs draw as a
 * thumbnail beside the description rather than the full-page stage the
 * defaults assume. The ceiling keeps the 4x6 label thumbnail-sized (the
 * default would blow it up to ~1150px tall); the floor sits below the global
 * legibility floor so the fit can keep shrinking the label into that narrow
 * stage instead of overflowing it into a horizontal scroll. The dialog stays
 * the place to inspect the artifact at full size. */
const SECTION_PREVIEW_MAX_ZOOM = 0.3;
const SECTION_PREVIEW_MIN_ZOOM = 0.2;
/** The thumbnail's width, per the specs. */
const SECTION_PREVIEW_WIDTH = 168;

export function ContainerLabelSection({
  container,
  onOpenLabel,
}: {
  container: ContainerLabelSectionContainer;
  /** Opens the label dialog, which owns the format choices, the print and
   * the download. */
  onOpenLabel: () => void;
}): ReactElement {
  return (
    <SectionCard title="Print or download label">
      {/* A thumbnail of the artifact beside a plain description of what is on
          it. Both the format choices and the download live in the dialog, so
          this card only has to say what the label is and offer the one way in. */}
      <Stack direction="row" spacing={3} alignItems="flex-start">
        <Box sx={{ width: SECTION_PREVIEW_WIDTH, flexShrink: 0 }}>
          <LabelPreviewStage
            content={container.label}
            layout={LabelFormat.Full}
            branded={true}
            size="sm"
            maxZoom={SECTION_PREVIEW_MAX_ZOOM}
            minZoom={SECTION_PREVIEW_MIN_ZOOM}
            stagePadding={0}
            showNotices={false}
            containerType={container.containerType}
            capacity={container.capacity}
            capacityUnit={container.capacityUnit}
          />
        </Box>
        <Stack spacing={2} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {labelSummarySentence(container.label, {
              containerType: container.containerType,
              capacity: container.capacity,
              capacityUnit: container.capacityUnit,
            })}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Print />}
            endIcon={<ChevronRight />}
            onClick={onOpenLabel}
          >
            Export options
          </Button>
        </Stack>
      </Stack>
      {/* Under the row, not in the thumbnail column: at 168px the notices
          wrapped to a few words per line. */}
      <LabelPreviewNotices
        content={container.label}
        layout={LabelFormat.Full}
        branded={true}
        containerType={container.containerType}
        capacity={container.capacity}
        capacityUnit={container.capacityUnit}
      />
    </SectionCard>
  );
}
